import React, { useState, useMemo } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions, reorderOptionsWithSelectedFirst } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, selectedOptions = [] } = props;
  const intl = useIntl();
  const [showAll, setShowAll] = useState(false);

  // Reorder options to show selected values at the top
  const reorderedOptions = reorderOptionsWithSelectedFirst(options, selectedOptions);

  const ITEMS_TO_SHOW = 5;
  const hasMoreItems = reorderedOptions.length > ITEMS_TO_SHOW;
  const itemsToDisplay = showAll ? reorderedOptions : reorderedOptions.slice(0, ITEMS_TO_SHOW);

  const handleShowMore = () => {
    setShowAll(true);
  };

  const handleShowLess = () => {
    setShowAll(false);
  };

  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {itemsToDisplay.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          const isSelected = selectedOptions.includes(option);
          return (
            <li key={fieldId} className={`${css.item} ${isSelected ? css.selectedItem : ''}`}>
              <FieldCheckbox id={fieldId} name={name} label={label} value={option} />
            </li>
          );
        })}
      </ul>
      {hasMoreItems && !showAll && (
        <button
          type="button"
          className={css.showMoreButton}
          onClick={handleShowMore}
        >
          {intl.formatMessage({ id: 'SelectMultipleFilter.showMore' }, { defaultMessage: 'Show more' })}
        </button>
      )}
      {hasMoreItems && showAll && (
        <button
          type="button"
          className={css.showMoreButton}
          onClick={handleShowLess}
        >
          {intl.formatMessage({ id: 'SelectMultipleFilter.showLess' }, { defaultMessage: 'Show less' })}
        </button>
      )}
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
// Note: has_any/has_all prefix is ONLY for multi-enum, not for enum
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  
  // Add searchMode prefix ONLY for multi-enum
  // For enum, Sharetribe API doesn't support multiple values, even without prefix
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  // Use useMemo to stabilize selectedOptions and prevent unnecessary re-renders
  const selectedOptions = useMemo(
    () => hasInitialValues ? parseSelectFilterOptions(initialValues[queryParamName]) : [],
    [hasInitialValues, initialValues, queryParamName]
  );

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  // Pass the initial values with the name key so that they can be passed to the correct field
  // Use useMemo to stabilize namedInitialValues and prevent React Final Form from marking as dirty
  const namedInitialValues = useMemo(
    () => ({ [name]: selectedOptions }),
    [name, selectedOptions]
  );

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    const formattedParams = format(usedValue, queryParamName, schemaType, searchMode);
    
    onSubmit(formattedParams);
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        selectedOptions={selectedOptions}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      keepDirtyOnReinitialize
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        selectedOptions={selectedOptions}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
